import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './Dashboard.css';

const Dashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        totalEntries: 0,
        recentMood: null,
        lastEntry: null
    });
    const [todayMood, setTodayMood] = useState(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [entriesRes, dailyRes] = await Promise.all([
                api.get('/journal?limit=1'),
                api.get(`/daily?from=${getTodayDate()}&to=${getTodayDate()}`)
            ]);

            setStats({
                totalEntries: entriesRes.data.pagination.total,
                lastEntry: entriesRes.data.entries[0]
            });

            if (dailyRes.data.logs.length > 0) {
                setTodayMood(dailyRes.data.logs[0].mood);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const handleMoodSelect = async (mood) => {
        try {
            await api.post('/daily', {
                date: getTodayDate(),
                mood
            });

            setTodayMood(mood);
            setShowConfetti(true);

            setTimeout(() => setShowConfetti(false), 3000);
        } catch (error) {
            console.error('Error saving mood:', error);
        }
    };

    if (loading) {
        return <div className="spinner-container"><div className="spinner"></div></div>;
    }

    return (
        <div className="dashboard">
            {showConfetti && (
                <div className="confetti">🎉 Great job tracking your mood! 🎉</div>
            )}

            <div className="dashboard-header">
                <h1>Welcome back, {user?.fullName?.split(' ')[0]}! 👋</h1>
                <p className="dashboard-subtitle">How are you feeling today?</p>
            </div>

            <div className="mood-selector">
                <button
                    className={`mood-btn ${todayMood === 'happy' ? 'selected' : ''}`}
                    onClick={() => handleMoodSelect('happy')}
                >
                    <span className="mood-emoji">😊</span>
                    <span>Happy</span>
                </button>
                <button
                    className={`mood-btn ${todayMood === 'neutral' ? 'selected' : ''}`}
                    onClick={() => handleMoodSelect('neutral')}
                >
                    <span className="mood-emoji">😐</span>
                    <span>Neutral</span>
                </button>
                <button
                    className={`mood-btn ${todayMood === 'sad' ? 'selected' : ''}`}
                    onClick={() => handleMoodSelect('sad')}
                >
                    <span className="mood-emoji">😔</span>
                    <span>Sad</span>
                </button>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon">📝</div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.totalEntries}</div>
                        <div className="stat-label">Journal Entries</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-content">
                        <div className="stat-value">{user?.lastVisited?.section || 'None'}</div>
                        <div className="stat-label">Last Section</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">📅</div>
                    <div className="stat-content">
                        <div className="stat-value">
                            {stats.lastEntry ? new Date(stats.lastEntry.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="stat-label">Last Entry</div>
                    </div>
                </div>
            </div>

            <div className="quick-actions">
                <h2>Quick Actions</h2>
                <div className="action-grid">
                    <a href="/journal/self-assessment" className="action-card">
                        <span className="action-icon">📝</span>
                        <span className="action-title">Self Assessment</span>
                    </a>
                    <a href="/journal/current-health" className="action-card">
                        <span className="action-icon">💊</span>
                        <span className="action-title">Current Health</span>
                    </a>
                    <a href="/journal/progress" className="action-card">
                        <span className="action-icon">📈</span>
                        <span className="action-title">Track Progress</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
